import { useNavigate } from "react-router-dom";
import { X, Plus, Minus, Trash2, ShoppingBag } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useShop } from "@/context/ShopContext";
import { imageUrl } from "@/lib/api";
import { ProductImagePlaceholder } from "./Logo";

export default function CartDrawer() {
  const { cart, cartOpen, setCartOpen, updateQuantity, removeItem, cartSubtotal, settings } = useShop();
  const navigate = useNavigate();
  const shipping = settings?.shipping_flat ?? 10.5;

  return (
    <AnimatePresence>
      {cartOpen && (
        <>
          <motion.div
            className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setCartOpen(false)}
            data-testid="cart-overlay"
          />
          <motion.aside
            className="fixed right-0 top-0 h-full w-full max-w-md z-[80] bg-[#121212] border-l border-[#D4AF37]/20 flex flex-col"
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between px-6 h-20 border-b border-[#D4AF37]/15">
              <h2 className="font-serif-display text-2xl text-white" data-testid="cart-drawer-title">Your Bag</h2>
              <button onClick={() => setCartOpen(false)} aria-label="Close cart" data-testid="cart-close-button"
                className="text-white/80 hover:text-[#D4AF37] transition-colors">
                <X size={22} strokeWidth={1.5} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
              {cart.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center gap-6" data-testid="cart-empty">
                  <ShoppingBag size={44} strokeWidth={1} className="text-[#D4AF37]/60" />
                  <p className="font-serif-display text-2xl text-white">Your bag is empty</p>
                  <p className="font-sans-body text-sm text-[#B8B8B8] font-light">
                    Discover a scent worth remembering.
                  </p>
                  <button
                    className="btn-gold"
                    data-testid="cart-continue-shopping-button"
                    onClick={() => { setCartOpen(false); navigate("/shop"); }}
                  >
                    Continue Shopping
                  </button>
                </div>
              ) : (
                cart.map((item) => (
                  <motion.div
                    key={`${item.product_id}-${item.size_label}`}
                    layout
                    initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                    className="flex gap-4 pb-6 border-b border-[#D4AF37]/10"
                    data-testid={`cart-item-${item.product_id}-${item.size_label.replace(/\s/g, "-")}`}
                  >
                    <div className="w-20 h-24 shrink-0 overflow-hidden gold-hairline">
                      {item.image_url ? (
                        <img src={imageUrl(item.image_url)} alt={item.name} className="w-full h-full object-cover" />
                      ) : (
                        <ProductImagePlaceholder name={item.name} className="w-full h-full" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="font-serif-display text-lg text-white leading-tight">{item.name}</p>
                          <p className="font-sans-body text-[11px] uppercase tracking-[0.2em] text-[#C6A15B] mt-1">{item.size_label}</p>
                        </div>
                        <button onClick={() => removeItem(item.product_id, item.size_label)} aria-label="Remove item"
                          data-testid={`cart-remove-${item.product_id}-${item.size_label.replace(/\s/g, "-")}`}
                          className="text-[#B8B8B8] hover:text-red-400 transition-colors">
                          <Trash2 size={16} strokeWidth={1.5} />
                        </button>
                      </div>
                      <div className="flex items-center justify-between mt-3">
                        <div className="flex items-center gold-hairline">
                          <button className="px-2.5 py-1.5 text-[#B8B8B8] hover:text-[#D4AF37]"
                            onClick={() => updateQuantity(item.product_id, item.size_label, item.quantity - 1)}
                            data-testid={`cart-qty-minus-${item.product_id}-${item.size_label.replace(/\s/g, "-")}`}
                            aria-label="Decrease quantity">
                            <Minus size={13} />
                          </button>
                          <span className="px-3 font-mono-price text-sm text-white" data-testid={`cart-qty-${item.product_id}-${item.size_label.replace(/\s/g, "-")}`}>
                            {item.quantity}
                          </span>
                          <button className="px-2.5 py-1.5 text-[#B8B8B8] hover:text-[#D4AF37]"
                            onClick={() => updateQuantity(item.product_id, item.size_label, item.quantity + 1)}
                            data-testid={`cart-qty-plus-${item.product_id}-${item.size_label.replace(/\s/g, "-")}`}
                            aria-label="Increase quantity">
                            <Plus size={13} />
                          </button>
                        </div>
                        <span className="font-mono-price text-[#D4AF37]" data-testid={`cart-line-total-${item.product_id}-${item.size_label.replace(/\s/g, "-")}`}>
                          ${(item.price * item.quantity).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </motion.div>
                ))
              )}
            </div>

            {cart.length > 0 && (
              <div className="px-6 py-6 border-t border-[#D4AF37]/15 space-y-4 bg-[#0D0D0D]">
                <div className="flex items-center justify-between font-sans-body text-sm text-[#B8B8B8]">
                  <span>Subtotal</span>
                  <span className="font-mono-price text-white" data-testid="cart-subtotal">${cartSubtotal.toFixed(2)}</span>
                </div>
                <div className="flex items-center justify-between font-sans-body text-sm text-[#B8B8B8]">
                  <span>Shipping (standard)</span>
                  <span className="font-mono-price text-white">${shipping.toFixed(2)}</span>
                </div>
                <p className="font-sans-body text-[11px] text-[#666666] font-light">
                  Taxes and express shipping calculated at checkout.
                </p>
                <button
                  className="btn-gold w-full"
                  data-testid="cart-checkout-button"
                  onClick={() => { setCartOpen(false); navigate("/checkout"); }}
                >
                  Proceed to Checkout
                </button>
                <button
                  className="w-full text-center font-sans-body text-xs uppercase tracking-[0.25em] text-[#B8B8B8] hover:text-[#D4AF37] transition-colors py-2"
                  data-testid="cart-keep-shopping-link"
                  onClick={() => setCartOpen(false)}
                >
                  Continue Shopping
                </button>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
